import { Component, ViewChild, inject, WritableSignal, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  MarketingOrder,
  Media,
  MultiMediaItem,
  SELECTEDORDER,
  UIConstants,
  cloneOrder,
  MarketingOrderMultimediaService,
  NotificationEvent,
  NotificationEventTypes,
  NotificationEventService,
  PromptDialogService,
  MultiMediaItemRequest,
  ToasterService,
  AppService,
  VideoLinkTipsConstants,
  VideoLinkPropertyWebsiteTipsConstants,
} from '@lc/core';
import { Store } from '@ngrx/store';
import { MultiMediaItemForm } from '@lc/shared-components-media';
import { FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { tap, filter, map, catchError } from 'rxjs/operators';
import { DialogProvider, IDialogOptions, Spinner, SpinnerComponent } from '@re-core/ngx-ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { BrandService } from '@lc/core/lib/services/brand.service';
import { E2eIdDirective } from '@lc/ui';
import { MultiMediaItemFormComponent } from '../multi-media-item-form/multi-media-item-form.component';
import { MultimediaTableComponent } from '../multimedia-table/multimedia-table.component';

@Component({
  selector: 'lc-manage-multimedia',
  templateUrl: './manage-multimedia.component.html',
  styleUrls: ['./manage-multimedia.component.scss'],
  host: { class: 'p-3 d-block spinner-container' },
  imports: [
    SpinnerComponent,
    MatIcon,
    MultiMediaItemFormComponent,
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MultimediaTableComponent,
    AsyncPipe,
    E2eIdDirective,
  ],
})
export class ManageMultimediaComponent {
  private store = inject<Store<any>>(Store);
  private dialogService = inject(PromptDialogService);
  private linkService = inject(MarketingOrderMultimediaService);
  private notificationService = inject(NotificationEventService);
  private readonly toasterService = inject(ToasterService);
  readonly videoLinkTips: WritableSignal<readonly string[]> = signal(
    VideoLinkTipsConstants,
  );
  readonly videoLinkPropertyWebsiteTips: WritableSignal<readonly string[]> = signal(
    VideoLinkPropertyWebsiteTipsConstants,
  );

  protected readonly AppService = AppService;

  addFormGroup = new MultiMediaItemForm();

  // Angular FormDirective needs to be reset after submission so that the error validations are removed.
  // See: https://github.com/angular/components/issues/4190
  @ViewChild(FormGroupDirective) formDirective: FormGroupDirective;

  marketingOrder$: Observable<MarketingOrder>;
  marketingOrder: MarketingOrder;
  spinner = Spinner.loading('Loading...');
  readonly brandService = inject(BrandService);

  constructor() {
    this.marketingOrder$ = this.store.select(SELECTEDORDER).pipe(
      filter((order) => order != null),
      map(cloneOrder),
      tap((order) => {
        this.marketingOrder = order;
        if (order && !order.media) {
          order.media = new Media();
        }
      }),
      tap(() => this.spinner.stop()),
      takeUntilDestroyed(),
    );
  }

  async addLink() {
    if (
      this.marketingOrder.media?.multimedia?.map((m) => m.uri).includes(this.addFormGroup.value.uri)
    ) {
      this.toasterService.showError('Cannot submit a duplicate link');
      return;
    }

    this.spinner.start('Adding link...');
    await this.linkService
      .addLink$(this.marketingOrder._id, new MultiMediaItemRequest(this.addFormGroup.value))
      .pipe(
        tap(() => {
          this.addFormGroup.reset();
          this.formDirective.resetForm();
        }),
        catchError(() => this.displayErrorNotification('There was an error adding this link')),
      )
      .toPromise();
    this.spinner.stop();
  }

  async updateItem(item: MultiMediaItem) {
    this.spinner.start('Adding link...');
    await this.linkService
      .updateLink$(this.marketingOrder._id, new MultiMediaItemRequest(item))
      .pipe(
        catchError(() => this.displayErrorNotification('There was an error updating this link')),
      )
      .toPromise();
    this.spinner.stop();
  }

  /**
   * Interrogates marketing order package for any products that may be using a multimedia item
   * @param item A MultiMediaItem
   */
  isUsedForProduct(item: MultiMediaItem) {
    const inUse = this.marketingOrder.selectedPackage.products
      .filter((product) => product.media && product.media.multimedia)
      .some((product) => product.media.multimedia.some((mmi) => mmi.title === item.title && mmi.uri === item.uri));
    return inUse;
  }

  async deleteSelected(selectedItems: MultiMediaItem[]) {
    // find any of the selectedItems that may be in use by products within the selected marketing order package
    const mediaInUse = selectedItems.filter((mmi) => this.isUsedForProduct(mmi));

    // notify user of multimedia in use.  Otherwise, delete selected
    if (mediaInUse && mediaInUse.length > 0) {
      let htmlMessage = 'The following multimedia items are in use by package products and cannot be deleted:<ul>';
      mediaInUse.forEach((mmi) => {
        htmlMessage += `<li>${mmi.title}</li>`;
      });
      htmlMessage += '</ul>';

      await this.dialogService.openPrompt('Multimedia item(s) in use', htmlMessage, UIConstants.OK);
    } else {
      const response = await this.dialogService.openPrompt(
        UIConstants.CONFIRM,
        'Are you sure you want to delete the selected multimedia link(s)?',
        UIConstants.YES,
        [UIConstants.NO],
      );
      if (response?.text === UIConstants.YES) {
        await this.linkService
          .deleteLinks$(
            this.marketingOrder._id,
            selectedItems.map((item) => item._id),
          )
          .toPromise();
      }
    }
  }

  private displayErrorNotification(message: string) {
    const event = new NotificationEvent(NotificationEventTypes.ERROR, message);
    this.notificationService.getEventEmitter().emit(event);
    return of(null);
  }

  static async open(data?: unknown) {
    const options: IDialogOptions = { width: '850px' };
    return await DialogProvider.openComponent<ManageMultimediaComponent>(
      ManageMultimediaComponent,
      data,
      options,
    );
  }
}
